import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react'
import omit from 'lodash/omit'
import isEqual from 'lodash/isEqual'

import FormWithControls from 'app/shared-components/FormWithControls'
import useFieldsWithClassName from 'app/hooks/useFieldsWithClassName'
import {
    RequestMethods,
} from 'app/enums/common.enums'
import Button from 'app/shared-components/Button'
import SharedContextProvider from 'app/shared-components/SharedContext'

import useTransportPlannerRoutes from 'app/SkyNetRoutes/hooks/useTransportPlannerRoutes'

import requestConfig from 'app/requests/TransportSimulation.requests'
import getFilteredSteps from './utils/getFilteredSteps'
import getDataState from './utils/getDataState'
import fields from './simulation.fields'
import useStyles from './Simulation.styles'
import getDataRequest from './utils/getDataRequest'
import useGetTemperatureSimulation from './hooks/useGetTemperatureSimulation'
import {
    SimulationData, SimulationForm,
} from './simulation.types'

type Props = {
    data?: SimulationData,
}

const defaultProps = {
    data: {},
}

const Simulation = (props: Props) => {
    const TransportPlannerRoutes = useTransportPlannerRoutes()
    const {
        classes,
    } = useStyles()

    const {
        data: savedSimulation,
    } = props

    const [
        state,
        setState,
    ] = useState<SimulationForm>(getDataState(savedSimulation))

    const [
        isDataChange,
        setIsDataChange,
    ] = useState<boolean>(false)

    useEffect(() => {
        document.querySelector('#mainScroll').scrollTop = 0
    }, [])

    const onChange = useCallback((newVal: SimulationForm) => {
        setState((prevState: SimulationForm) => {
            if (!isDataChange
                && (!isEqual(prevState.selectedContainer, newVal.selectedContainer)
                    || !isEqual(
                        getFilteredSteps(prevState.simulationSteps),
                        getFilteredSteps(newVal.simulationSteps),
                    ))
            ) {
                setIsDataChange(true)
            }
            return newVal
        })
    }, [isDataChange])

    const fieldsWithClassName = useFieldsWithClassName(fields, classes)

    const {
        data: simulationResults,
    } = useGetTemperatureSimulation(state, isDataChange)

    const modifyDataBeforeSend = (requestData: SimulationForm) => {
        const steps = getFilteredSteps(requestData.simulationSteps)
            .map((item) => {
                return {
                    ...omit(item, 'id'),
                }
            })

        const dataRequest = getDataRequest({
            requestData, simulationSteps: steps,
        })

        if (!requestData.selectedContainer?.productType || !steps.length) {
            return {
                ...dataRequest,
            }
        }

        return {
            ...dataRequest,
        }
    }

    const onSuccess = useCallback(({
        action,
        id,
    }: {
        action: RequestMethods,
        id: number,
    }): void => {
        if (action === RequestMethods.CREATE) {
            TransportPlannerRoutes.Simulation.navigate({
                params: {
                    id,
                },
            })
        }
        if (action === RequestMethods.DELETE) {
            TransportPlannerRoutes.NewSimulation.navigate()
        }
    }, [TransportPlannerRoutes])

    const duplicate = useCallback((): void => {
        const data = omit(savedSimulation, [
            'id',
            'simulationResults',
            'simulationNumber',
            'selectedContainer',
        ])

        TransportPlannerRoutes.NewSimulation.navigate({
            options: {
                state: data,
            },
        })
    }, [
        TransportPlannerRoutes,
        savedSimulation,
    ])

    const customButtons: JSX.Element[] = useMemo(() => {
        if (!state.id) {
            return []
        }

        return [<Button
            label="Duplicate"
            name="Duplicate"
            secondary
            onClick={duplicate}
        />]
    }, [
        duplicate,
        state.id,
    ])

    return (
        <SharedContextProvider
            value={{
                selectedContainer: state.selectedContainer,
                simulationResults,
                simulationSteps: getFilteredSteps(state.simulationSteps),
                isDataChange,
                setIsDataChange,
            }}
        >
            <FormWithControls
                className={classes.rowFormContainer}
                requestParams={
                    state.id
                        ? requestConfig.simulationUpdate()
                        : requestConfig.simulationCreate()
                }
                classNames={{
                    gridWrapper: classes.gridWrapper,
                    title: classes.headerTitle,
                }}
                value={state}
                setExternalValue={onChange}
                name={requestConfig.name}
                fields={fieldsWithClassName}
                modifyDataBeforeSend={modifyDataBeforeSend}
                customButtons={customButtons}
                onSuccess={onSuccess}
                exists={Boolean(state.id)}
            />
        </SharedContextProvider>
    )
}

Simulation.defaultProps = defaultProps

export default Simulation
